import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { dashboardAPI } from '../lib/api';
import api from '../lib/api';
import { 
    Truck, Users, Wrench, Fuel, Route, AlertTriangle, 
    TrendingUp, TrendingDown, Activity, Loader2, Bell 
} from 'lucide-react';
import { 
    LineChart, Line, BarChart, Bar, XAxis, YAxis, 
    CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell 
} from 'recharts';
import { toast } from 'sonner';
import MaintenanceAlertsModal from '../components/maintenance/MaintenanceAlertsModal';
import MaintenanceAlertSettingsModal from '../components/maintenance/MaintenanceAlertSettingsModal';

const KPICard = ({ icon: Icon, label, value, subValue, trend, color = 'yellow' }) => {
    const colorClasses = {
        yellow: 'text-[#1B7EB5]',
        green: 'text-green-500',
        red: 'text-red-500',
        blue: 'text-blue-500'
    };

    return (
        <div className="kpi-card animate-fade-in">
            <div className="flex items-start justify-between mb-3">
                <div className={`w-10 h-10 rounded-sm flex items-center justify-center ${color === 'yellow' ? 'bg-[#1B7EB5]/10' : color === 'green' ? 'bg-green-500/10' : color === 'red' ? 'bg-red-500/10' : 'bg-blue-500/10'}`}>
                    <Icon className={`w-5 h-5 ${colorClasses[color]}`} />
                </div>
                {trend && (
                    <div className={`flex items-center gap-1 text-xs ${trend > 0 ? 'text-green-500' : 'text-red-500'}`}>
                        {trend > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                        {Math.abs(trend)}%
                    </div>
                )}
            </div>
            <p className="overline mb-1">{label}</p>
            <p className="kpi-value text-white">{value}</p>
            {subValue && <p className="text-sm text-zinc-500 mt-1">{subValue}</p>}
        </div>
    );
};

const ChartCard = ({ title, children, className = '' }) => (
    <div className={`fleet-card p-4 ${className}`}>
        <h3 className="text-sm font-semibold text-slate-700 mb-4">{title}</h3>
        {children}
    </div>
);

export default function DashboardPage() {
    const { user } = useAuth();
    const { t, language } = useLanguage();
    const [stats, setStats] = useState(null);
    const [fuelTrends, setFuelTrends] = useState([]);
    const [maintenanceTrends, setMaintenanceTrends] = useState([]);
    const [vehicleStatus, setVehicleStatus] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Maintenance alerts state
    const [alertsModalOpen, setAlertsModalOpen] = useState(false);
    const [settingsModalOpen, setSettingsModalOpen] = useState(false);
    const [maintenanceAlerts, setMaintenanceAlerts] = useState(null);

    useEffect(() => {
        fetchDashboardData();
        fetchMaintenanceAlerts();
    }, []);

    const fetchDashboardData = async () => {
        try {
            const [statsRes, fuelRes, maintenanceRes, statusRes] = await Promise.all([
                dashboardAPI.getStats(),
                dashboardAPI.getFuelTrends(),
                dashboardAPI.getMaintenanceTrends(),
                dashboardAPI.getVehicleStatus()
            ]);
            setStats(statsRes.data);
            setFuelTrends(fuelRes.data);
            setMaintenanceTrends(maintenanceRes.data);
            setVehicleStatus(statusRes.data);
        } catch (error) {
            console.error('Failed to fetch dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchMaintenanceAlerts = async () => {
        try {
            const response = await api.get('/maintenance-alerts');
            setMaintenanceAlerts(response.data);
            
            // Show toast notifications for alerts
            const totalAlerts = response.data.overdue_count + response.data.urgent_count;
            if (totalAlerts > 0) {
                // Show popup modal if there are overdue or urgent alerts
                if (response.data.overdue_count > 0) {
                    setAlertsModalOpen(true);
                    toast.error(
                        language === 'el' 
                            ? `⚠️ ${response.data.overdue_count} εκπρόθεσμες συντηρήσεις!`
                            : `⚠️ ${response.data.overdue_count} overdue maintenance items!`,
                        { duration: 5000 }
                    );
                } else if (response.data.urgent_count > 0) {
                    setAlertsModalOpen(true);
                    toast.warning(
                        language === 'el' 
                            ? `🔔 ${response.data.urgent_count} επείγουσες συντηρήσεις`
                            : `🔔 ${response.data.urgent_count} urgent maintenance items`,
                        { duration: 4000 }
                    );
                }
            } else if (response.data.upcoming_count > 0) {
                // Just a toast for upcoming (non-urgent) maintenance
                toast.info(
                    language === 'el' 
                        ? `📅 ${response.data.upcoming_count} επερχόμενες συντηρήσεις`
                        : `📅 ${response.data.upcoming_count} upcoming maintenance items`,
                    { duration: 3000 }
                );
            }
        } catch (error) {
            console.error('Failed to fetch maintenance alerts:', error);
        }
    };

    const COLORS = ['#22C55E', '#EAB308', '#EF4444', '#3B82F6'];

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0
        }).format(value);
    };

    const formatMonth = (month) => {
        const [year, m] = month.split('-');
        const date = new Date(year, parseInt(m) - 1);
        return date.toLocaleDateString(language === 'el' ? 'el-GR' : 'en-US', { month: 'short' });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-96">
                <Loader2 className="w-8 h-8 text-[#1B7EB5] animate-spin" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="dashboard-page">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">{t('nav.dashboard')}</h1>
                    <p className="text-zinc-500">{t('dashboard.welcomeBack')}, {user?.name}</p>
                </div>
                <div className="flex items-center gap-4">
                    {/* Maintenance Alerts Button */}
                    {maintenanceAlerts && (maintenanceAlerts.overdue_count > 0 || maintenanceAlerts.urgent_count > 0 || maintenanceAlerts.upcoming_count > 0) && (
                        <button
                            onClick={() => setAlertsModalOpen(true)}
                            className={`relative flex items-center gap-2 px-3 py-2 rounded-lg transition-colors ${
                                maintenanceAlerts.overdue_count > 0 
                                    ? 'bg-red-100 text-red-700 hover:bg-red-200'
                                    : maintenanceAlerts.urgent_count > 0
                                    ? 'bg-amber-100 text-amber-700 hover:bg-amber-200'
                                    : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                            }`}
                            data-testid="maintenance-alerts-btn"
                        >
                            <Bell className="w-4 h-4" />
                            <span className="text-sm font-medium">
                                {language === 'el' ? 'Συντήρηση' : 'Maintenance'}
                            </span>
                            <span className={`absolute -top-1 -right-1 w-5 h-5 text-xs font-bold rounded-full flex items-center justify-center ${
                                maintenanceAlerts.overdue_count > 0 
                                    ? 'bg-red-500 text-white'
                                    : maintenanceAlerts.urgent_count > 0
                                    ? 'bg-amber-500 text-white'
                                    : 'bg-blue-500 text-white'
                            }`}>
                                {maintenanceAlerts.overdue_count + maintenanceAlerts.urgent_count + maintenanceAlerts.upcoming_count}
                            </span>
                        </button>
                    )}
                    <div className="flex items-center gap-2 text-sm text-zinc-500">
                        <Activity className="w-4 h-4 text-green-500 animate-pulse-slow" />
                        <span>{language === 'el' ? 'Δεδομένα σε πραγματικό χρόνο' : 'Real-time data'}</span>
                    </div>
                </div>
            </div>

            {/* KPI Grid */}
            <div className="dashboard-grid">
                <KPICard 
                    icon={Truck}
                    label={t('dashboard.totalVehicles')}
                    value={stats?.vehicles?.total || 0}
                    subValue={`${stats?.vehicles?.active || 0} ${t('common.active').toLowerCase()}`}
                    color="yellow"
                />
                <KPICard 
                    icon={Users}
                    label={language === 'el' ? 'Συνολικοί Οδηγοί' : 'Total Drivers'}
                    value={stats?.drivers?.total || 0}
                    subValue={`${stats?.drivers?.available || 0} ${language === 'el' ? 'διαθέσιμοι' : 'available'}`}
                    color="green"
                />
                <KPICard 
                    icon={Wrench}
                    label={t('dashboard.pendingMaintenance')}
                    value={stats?.maintenance?.pending || 0}
                    subValue={stats?.maintenance?.overdue > 0 ? `${stats.maintenance.overdue} ${t('maintenance.overdue').toLowerCase()}` : (language === 'el' ? 'Όλα στο πρόγραμμα' : 'All on schedule')}
                    color={stats?.maintenance?.overdue > 0 ? 'red' : 'yellow'}
                />
                <KPICard 
                    icon={Route}
                    label={language === 'el' ? 'Ενεργές Διαδρομές' : 'Active Trips'}
                    value={stats?.trips?.active || 0}
                    subValue={`${stats?.trips?.completed || 0} ${t('common.completed').toLowerCase()}`}
                    color="blue"
                />
            </div>

            {/* Fuel Stats */}
            <div className="dashboard-grid">
                <KPICard 
                    icon={Fuel}
                    label={language === 'el' ? 'Κόστος Καυσίμων (30 Ημέρες)' : 'Fuel Cost (30 Days)'}
                    value={formatCurrency(stats?.fuel?.total_cost_30_days || 0)}
                    subValue={`${(stats?.fuel?.total_liters_30_days || 0).toLocaleString()} ${language === 'el' ? 'λίτρα' : 'liters'}`}
                    color="yellow"
                />
                <KPICard 
                    icon={AlertTriangle}
                    label={language === 'el' ? 'Εκπρόθεσμη Συντήρηση' : 'Overdue Maintenance'}
                    value={stats?.maintenance?.overdue || 0}
                    subValue={language === 'el' ? 'Απαιτεί άμεση προσοχή' : 'Requires immediate attention'}
                    color="red"
                />
            </div>

            {/* Charts */}
            <div className="bento-grid">
                {/* Fuel Cost Trends */}
                <ChartCard title={language === 'el' ? 'Τάσεις Κόστους Καυσίμων (6 Μήνες)' : 'Fuel Cost Trends (6 Months)'} className="bento-span-8">
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={fuelTrends}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                                <XAxis 
                                    dataKey="month" 
                                    tickFormatter={formatMonth}
                                    stroke="#71717A"
                                    tick={{ fill: '#A1A1AA', fontSize: 12 }}
                                />
                                <YAxis 
                                    stroke="#71717A"
                                    tick={{ fill: '#A1A1AA', fontSize: 12 }}
                                    tickFormatter={(value) => `$${value}`}
                                />
                                <Tooltip 
                                    contentStyle={{ 
                                        backgroundColor: '#121214', 
                                        border: '1px solid #27272A',
                                        borderRadius: '2px'
                                    }}
                                    labelStyle={{ color: '#FAFAFA' }}
                                    formatter={(value) => [`$${value}`, language === 'el' ? 'Κόστος' : 'Cost']}
                                    labelFormatter={formatMonth}
                                />
                                <Line 
                                    type="linear" 
                                    dataKey="cost" 
                                    stroke="#EAB308" 
                                    strokeWidth={2}
                                    dot={{ fill: '#EAB308', strokeWidth: 0, r: 4 }}
                                    activeDot={{ r: 6, fill: '#EAB308' }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </ChartCard>

                {/* Vehicle Status */}
                <ChartCard title={language === 'el' ? 'Κατάσταση Οχημάτων' : 'Vehicle Status'} className="bento-span-4">
                    <div className="h-64 flex items-center justify-center">
                        {vehicleStatus.length > 0 ? (
                            <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                    <Pie
                                        data={vehicleStatus}
                                        cx="50%"
                                        cy="50%"
                                        innerRadius={60}
                                        outerRadius={80}
                                        paddingAngle={2}
                                        dataKey="count"
                                        nameKey="status"
                                    >
                                        {vehicleStatus.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip 
                                        contentStyle={{ 
                                            backgroundColor: '#121214', 
                                            border: '1px solid #27272A',
                                            borderRadius: '2px'
                                        }}
                                        labelStyle={{ color: '#FAFAFA' }}
                                    />
                                </PieChart>
                            </ResponsiveContainer>
                        ) : (
                            <p className="text-zinc-500 text-sm">{t('common.noData')}</p>
                        )}
                    </div>
                    <div className="mt-4 space-y-2">
                        {vehicleStatus.map((item, index) => (
                            <div key={item.status} className="flex items-center justify-between text-sm">
                                <div className="flex items-center gap-2">
                                    <div 
                                        className="w-3 h-3 rounded-sm" 
                                        style={{ backgroundColor: COLORS[index % COLORS.length] }}
                                    />
                                    <span className="text-zinc-400 capitalize">{item.status}</span>
                                </div>
                                <span className="text-white font-medium">{item.count}</span>
                            </div>
                        ))}
                    </div>
                </ChartCard>

                {/* Maintenance Cost Trends */}
                <ChartCard title={language === 'el' ? 'Κόστη Συντήρησης (6 Μήνες)' : 'Maintenance Costs (6 Months)'} className="bento-span-6">
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={maintenanceTrends}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                                <XAxis 
                                    dataKey="month" 
                                    tickFormatter={formatMonth}
                                    stroke="#71717A"
                                    tick={{ fill: '#A1A1AA', fontSize: 12 }}
                                />
                                <YAxis 
                                    stroke="#71717A"
                                    tick={{ fill: '#A1A1AA', fontSize: 12 }}
                                    tickFormatter={(value) => `$${value}`}
                                />
                                <Tooltip 
                                    contentStyle={{ 
                                        backgroundColor: '#121214', 
                                        border: '1px solid #27272A',
                                        borderRadius: '2px'
                                    }}
                                    labelStyle={{ color: '#FAFAFA' }}
                                    formatter={(value, name) => [name === 'cost' ? `$${value}` : value, name === 'cost' ? (language === 'el' ? 'Κόστος' : 'Cost') : (language === 'el' ? 'Πλήθος' : 'Count')]}
                                    labelFormatter={formatMonth}
                                />
                                <Bar dataKey="cost" fill="#EF4444" radius={[2, 2, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </ChartCard>

                {/* Fuel Consumption */}
                <ChartCard title={language === 'el' ? 'Κατανάλωση Καυσίμων (Λίτρα)' : 'Fuel Consumption (Liters)'} className="bento-span-6">
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={fuelTrends}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                                <XAxis 
                                    dataKey="month" 
                                    tickFormatter={formatMonth}
                                    stroke="#71717A"
                                    tick={{ fill: '#A1A1AA', fontSize: 12 }}
                                />
                                <YAxis 
                                    stroke="#71717A"
                                    tick={{ fill: '#A1A1AA', fontSize: 12 }}
                                />
                                <Tooltip 
                                    contentStyle={{ 
                                        backgroundColor: '#121214', 
                                        border: '1px solid #27272A',
                                        borderRadius: '2px'
                                    }}
                                    labelStyle={{ color: '#FAFAFA' }}
                                    formatter={(value) => [`${value} L`, language === 'el' ? 'Λίτρα' : 'Liters']}
                                    labelFormatter={formatMonth}
                                />
                                <Bar dataKey="liters" fill="#22C55E" radius={[2, 2, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </ChartCard>
            </div>

            {/* Maintenance Alerts Modal */}
            <MaintenanceAlertsModal
                open={alertsModalOpen}
                onOpenChange={setAlertsModalOpen}
                language={language}
                onSettingsClick={() => {
                    setAlertsModalOpen(false);
                    setSettingsModalOpen(true);
                }}
            />

            {/* Maintenance Alert Settings Modal */}
            <MaintenanceAlertSettingsModal
                open={settingsModalOpen}
                onOpenChange={setSettingsModalOpen}
                language={language}
                onSave={() => fetchMaintenanceAlerts()}
            />
        </div>
    );
}
